import { useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import type { FlightSeries } from "../lib/api";
import { shapeSeries, type SeriesBar } from "../lib/history";

/**
 * One flight across the range, a day at a time: the airline's passengers and what the
 * checkpoint counted as paired bars against a dashed seats line. Drawn the way the
 * Forecast chart is drawn, in a stretched SVG box with strokes that keep their width, so
 * it reflows with the table and stays legible at any width.
 */
export function FlightSeriesChart({ series, tz }: { series: FlightSeries; tz: string }) {
  const [at, setAt] = useState<string | null>(null);
  const shape = shapeSeries(series.days, series.seats);
  const hovered = shape.bars.find((b) => b.date === at) ?? null;
  const label = (date: string) => DateTime.fromISO(date, { zone: tz }).toFormat("ccc d LLL");
  const dep = series.direction === "DEP";

  if (!shape.measured) {
    return <p className="py-2 text-[12.5px] text-ink-3">Nothing measured for this flight in the range.</p>;
  }

  // Each bar's pair sits inside its day's slot with a little air either side; the slot
  // is the hover target, so a thin bar is as easy to point at as a tall one.
  const inset = shape.bars[0] ? shape.bars[0].w * 0.15 : 0;
  const pair = (b: SeriesBar) => ({ paxX: b.x + inset, width: dep ? (b.w - 2 * inset) / 2 : b.w - 2 * inset });

  return (
    <div className="py-2" onMouseLeave={() => setAt(null)}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[12.5px] font-semibold">
          {series.flight} day by day, {series.days.length} {series.days.length === 1 ? "day" : "days"}
        </div>
        {series.seats != null && <div className="text-[11.5px] text-ink-3">Dashed line: {series.seats} seats on the type it usually flies</div>}
      </div>

      <div className="flex gap-2">
        <div className="relative h-36 w-8 shrink-0">
          {shape.ticks.map((v) => (
            <div key={v} className="absolute right-0 -translate-y-1/2 text-[10px] text-ink-3" style={{ bottom: `${(v / shape.top) * 100}%` }}>
              {v}
            </div>
          ))}
        </div>

        <div className="relative flex-1">
          <div className="relative h-36 border-b border-line">
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
              {shape.ticks.map((v) => (
                <line key={v} x1={0} x2={100} y1={100 - (v / shape.top) * 100} y2={100 - (v / shape.top) * 100} className="stroke-line/60" strokeWidth={1} vectorEffect="non-scaling-stroke" />
              ))}
              {shape.bars.map((b) => {
                const { paxX, width } = pair(b);
                return (
                  <g key={b.date} className={clsx(at === b.date ? "opacity-100" : "opacity-85")}>
                    <Bar x={paxX} w={width} h={b.pax} fill="fill-info" stroke="stroke-info" />
                    {dep && <Bar x={paxX + width} w={width} h={b.s2} fill="fill-ink-3/70" stroke="stroke-ink-3/70" />}
                  </g>
                );
              })}
              {shape.seatsY != null && (
                <line x1={0} x2={100} y1={100 - shape.seatsY} y2={100 - shape.seatsY} className="stroke-ink-3" strokeWidth={2} strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
              )}
            </svg>
            <div className="absolute inset-0 flex">
              {shape.bars.map((b) => (
                <button
                  key={b.date}
                  className="h-full flex-1 cursor-default"
                  onMouseEnter={() => setAt(b.date)}
                  onFocus={() => setAt(b.date)}
                  aria-label={`${label(b.date)}: ${b.day.cancelled ? "cancelled" : `${b.day.airlinePax ?? "no"} airline passengers`}`}
                />
              ))}
            </div>
          </div>

          <div className="relative mt-1 h-4">
            {shape.bars
              .filter((_, i, all) => all.length <= 10 || i % Math.ceil(all.length / 10) === 0)
              .map((b) => (
                <span key={b.date} className="absolute -translate-x-1/2 text-[10px] text-ink-3" style={{ left: `${b.x + b.w / 2}%` }}>
                  {DateTime.fromISO(b.date, { zone: tz }).toFormat("d LLL")}
                </span>
              ))}
          </div>

          {hovered && (
            <div
              className="pointer-events-none absolute top-0 z-20 w-52 rounded-lg border border-line bg-panel p-2 text-[11.5px] shadow-lg"
              style={{ left: `min(max(0px, calc(${hovered.x + hovered.w / 2}% - 104px)), calc(100% - 208px))` }}
            >
              <div className="font-semibold">
                {label(hovered.date)}
                {hovered.cancelled && <span className="ml-1 text-bad">cancelled</span>}
              </div>
              <Readout label="Seats" value={hovered.day.seats} />
              <Readout label="Passengers (airline)" value={hovered.day.airlinePax} />
              {dep && <Readout label="Through security" value={hovered.day.s2} />}
              <Readout label="Bags" value={hovered.day.bags} />
              <Readout label="Delay" value={hovered.day.delayMin} unit=" min" signed />
            </div>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-ink-3">
        <span className="flex items-center gap-1">
          <span className="inline-block size-2.5 rounded-sm bg-info" /> airline passengers
        </span>
        {dep && (
          <span className="flex items-center gap-1">
            <span className="inline-block size-2.5 rounded-sm bg-ink-3/70" /> through security
          </span>
        )}
        {shape.seatsY != null && (
          <span className="flex items-center gap-1">
            <span className="inline-block h-0 w-4 border-t-2 border-dashed border-ink-3" /> seats
          </span>
        )}
      </div>
    </div>
  );
}

/** A zero that was counted is drawn as a hairline; a day with no count gets nothing. */
function Bar({ x, w, h, fill, stroke }: { x: number; w: number; h: number | null; fill: string; stroke: string }) {
  if (h == null) return null;
  if (h <= 0) return <line x1={x} x2={x + w} y1={100} y2={100} className={stroke} strokeWidth={2} vectorEffect="non-scaling-stroke" />;
  return <rect x={x} y={100 - h} width={w} height={h} className={fill} />;
}

function Readout({ label, value, unit = "", signed = false }: { label: string; value: number | null; unit?: string; signed?: boolean }) {
  return (
    <div className="flex justify-between gap-3 text-ink-2">
      <span>{label}</span>
      <span className="tabular text-ink">{value == null ? "—" : `${signed && value > 0 ? "+" : ""}${value.toLocaleString()}${unit}`}</span>
    </div>
  );
}
